import React, { useState } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import { useParams, useNavigate } from 'react-router-dom';
import { JobHttpService } from 'cms/features/feature_job/service';
import ContractForm from '../components/ContractForm';
import {
    Box,
    Container,
    Card,
    CardContent,
    Typography,
    CircularProgress,
    Alert,
    Button,
    Grid,
    IconButton
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import DownloadIcon from '@mui/icons-material/Download';
import PrintIcon from '@mui/icons-material/Print';
import DescriptionIcon from '@mui/icons-material/Description';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import PersonIcon from '@mui/icons-material/Person';
import WorkIcon from '@mui/icons-material/Work';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';

function ContractDetailsPage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const [editFormOpen, setEditFormOpen] = useState(false);

    const { data: contract, isLoading, isError, error } = useQuery(
        ['contract', id],
        () => JobHttpService.loadContract(id),
        { enabled: !!id }
    );

    const handleBack = () => {
        navigate('/employer/contracts');
    };

    const handleEditClick = () => {
        setEditFormOpen(true);
    };

    const handleFormClose = () => {
        setEditFormOpen(false);
    };

    const handleContractSaved = () => {
        queryClient.invalidateQueries(['contract', id]);
        queryClient.invalidateQueries(['contracts']);
        setEditFormOpen(false);
    };

    const handleDownload = async () => {
        try {
            const blob = await JobHttpService.generateContractPdf(id);
            
            if (!blob || blob.type !== 'application/pdf') {
                throw new Error('Invalid PDF response');
            }
            
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `contract-${id}.pdf`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (err) {
            console.error('Error downloading PDF:', err);
            alert('Failed to download PDF. Please try again.');
        }
    };

    const handlePrint = () => {
        navigate(`/employer/contracts/${id}/print`);
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'Not set';
        return new Date(dateString).toLocaleDateString();
    };

    if (isLoading) {
        return (
            <section className="section-box mt-50">
                <Container maxWidth="lg">
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                        <CircularProgress />
                    </Box>
                </Container>
            </section>
        );
    }

    if (isError || !contract) {
        return (
            <section className="section-box mt-50">
                <Container maxWidth="lg">
                    <Alert severity="error">
                        {error?.message || 'Failed to load contract'}
                    </Alert>
                    <Button
                        startIcon={<ArrowBackIcon />}
                        onClick={handleBack}
                        sx={{ mt: 2 }}
                    >
                        Back to Contracts
                    </Button>
                </Container>
            </section>
        );
    }

    const employee = contract.employee;
    const employeeName = employee 
        ? `${employee.first_name || ''} ${employee.last_name || ''}`.trim()
        : 'Unknown Employee';

    return (
        <section className="section-box mt-50">
            <Container maxWidth="lg">
                <Button
                    startIcon={<ArrowBackIcon />}
                    onClick={handleBack}
                    sx={{ mb: 3 }}
                >
                    Back
                </Button>

                {/* Contract Header Card */}
                <Card sx={{ mb: 3 }}>
                    <CardContent sx={{ p: 3 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <DescriptionIcon sx={{ fontSize: 48, color: 'primary.main' }} />
                                <Box>
                                    <Typography variant="h5" fontWeight={600} gutterBottom>
                                        Contract {contract.contract_number || `#${contract.id}`}
                                    </Typography>
                                </Box>
                            </Box>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                                <IconButton
                                    onClick={handleEditClick}
                                    color="primary"
                                >
                                    <EditIcon />
                                </IconButton>
                                <Button
                                    variant="outlined"
                                    startIcon={<PrintIcon />}
                                    onClick={handlePrint}
                                    sx={{ mr: 1 }}
                                >
                                    View & Print
                                </Button>
                                <Button
                                    variant="contained"
                                    startIcon={<DownloadIcon />}
                                    onClick={handleDownload}
                                >
                                    Download PDF
                                </Button>
                            </Box>
                        </Box>

                        <Grid container spacing={3}>
                            <Grid item xs={12} sm={6} md={4}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                    <PersonIcon color="action" />
                                    <Typography variant="subtitle2" color="text.secondary">
                                        Employee
                                    </Typography>
                                </Box>
                                <Typography variant="body1" fontWeight={500}>
                                    {employeeName}
                                </Typography>
                            </Grid>
                            <Grid item xs={12} sm={6} md={4}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                    <WorkIcon color="action" />
                                    <Typography variant="subtitle2" color="text.secondary">
                                        Position
                                    </Typography>
                                </Box>
                                <Typography variant="body1" fontWeight={500}>
                                    {contract.employee_position_title || '—'}
                                </Typography>
                            </Grid>
                            <Grid item xs={12} sm={6} md={4}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                    <CalendarTodayIcon color="action" />
                                    <Typography variant="subtitle2" color="text.secondary">
                                        Start Date
                                    </Typography>
                                </Box>
                                <Typography variant="body1">
                                    {formatDate(contract.contract_start_date)}
                                </Typography>
                            </Grid>
                            <Grid item xs={12} sm={6} md={4}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                    <CalendarTodayIcon color="action" />
                                    <Typography variant="subtitle2" color="text.secondary">
                                        End Date
                                    </Typography>
                                </Box>
                                <Typography variant="body1">
                                    {formatDate(contract.contract_end_date)}
                                </Typography>
                            </Grid>
                            {contract.employment_type && (
                                <Grid item xs={12} sm={6} md={4}>
                                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                        Employment Type
                                    </Typography>
                                    <Typography variant="body1">
                                        {contract.employment_type}
                                    </Typography>
                                </Grid>
                            )}
                            {contract.employment_hours_per_week && (
                                <Grid item xs={12} sm={6} md={4}>
                                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                        Hours per Week
                                    </Typography>
                                    <Typography variant="body1">
                                        {contract.employment_hours_per_week}
                                    </Typography>
                                </Grid>
                            )}
                            {contract.salary && (
                                <Grid item xs={12} sm={6} md={4}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                        <AttachMoneyIcon color="action" />
                                        <Typography variant="subtitle2" color="text.secondary">
                                            Salary
                                        </Typography>
                                    </Box>
                                    <Typography variant="body1" fontWeight={500}>
                                        ${contract.salary.toLocaleString()}
                                    </Typography>
                                </Grid>
                            )}
                        </Grid>

                        {(contract.contract_details || contract.employment_place || contract.employment_notice_period || 
                          contract.holiday_entitlement || contract.collective_agreement || contract.insurance_details) && (
                            <Box sx={{ mt: 3 }}>
                                <Typography variant="h6" gutterBottom>
                                    Additional Details
                                </Typography>
                                <Grid container spacing={2}>
                                    {contract.employment_place && (
                                        <Grid item xs={12} sm={6}>
                                            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                                Employment Place
                                            </Typography>
                                            <Typography variant="body2">
                                                {contract.employment_place}
                                            </Typography>
                                        </Grid>
                                    )}
                                    {contract.employment_notice_period && (
                                        <Grid item xs={12} sm={6}>
                                            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                                Notice Period
                                            </Typography>
                                            <Typography variant="body2">
                                                {contract.employment_notice_period}
                                            </Typography>
                                        </Grid>
                                    )}
                                    {contract.holiday_entitlement && (
                                        <Grid item xs={12} sm={6}>
                                            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                                Holiday Entitlement
                                            </Typography>
                                            <Typography variant="body2">
                                                {contract.holiday_entitlement}
                                            </Typography>
                                        </Grid>
                                    )}
                                    {contract.collective_agreement && (
                                        <Grid item xs={12} sm={6}>
                                            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                                Collective Agreement
                                            </Typography>
                                            <Typography variant="body2">
                                                {contract.collective_agreement}
                                            </Typography>
                                        </Grid>
                                    )}
                                    {contract.insurance_details && (
                                        <Grid item xs={12}>
                                            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                                Insurance Details
                                            </Typography>
                                            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                                                {contract.insurance_details}
                                            </Typography>
                                        </Grid>
                                    )}
                                    {contract.contract_details && (
                                        <Grid item xs={12}>
                                            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                                Contract Details
                                            </Typography>
                                            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                                                {contract.contract_details}
                                            </Typography>
                                        </Grid>
                                    )}
                                </Grid>
                            </Box>
                        )}
                    </CardContent>
                </Card>

                {/* Edit Form Modal */}
                <ContractForm
                    open={editFormOpen}
                    onClose={handleFormClose}
                    contractId={id}
                    companyId={contract.company?.id}
                    onSaved={handleContractSaved}
                />
            </Container>
        </section>
    );
}

export default ContractDetailsPage;
